import React, { useState, useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  ListChecks,
  Loader2,
  CalendarDays,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../../components/shared';
import { useVendorBookings } from '../../services/vendorBookingService';
import { useNavigate } from 'react-router-dom';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const VendorCalendar = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());

  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Fetch all bookings for the calendar (date range + high page size)
  const monthStart = new Date(currentYear, currentMonth, 1);
  const monthEnd = new Date(currentYear, currentMonth + 1, 0);
  const calendarParams = {
    calendar: 1,
    start_date: monthStart.toISOString().slice(0, 10),
    end_date: monthEnd.toISOString().slice(0, 10),
    per_page: 500,
  };
  const { data: bookingsResponse, isLoading, error } = useVendorBookings(calendarParams);
  const bookings = useMemo(() => bookingsResponse?.data?.data || [], [bookingsResponse?.data?.data]);

  // Get first day of month and number of days
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dayBookings = bookings.filter(booking => {
        const bookingDate = new Date(booking.event_date);
        return bookingDate.toDateString() === date.toDateString();
      });

      days.push({
        day,
        date,
        bookings: dayBookings
      });
    }

    return days;
  }, [currentMonth, currentYear, bookings, daysInMonth, startingDayOfWeek]);

  // Navigation functions
  const goToPreviousMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get month name
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getStatusColor = (status) => {
    const colors = {
      pending:
        'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800/50',
      confirmed:
        'bg-primary-50 text-primary-800 border-primary-200 dark:bg-primary-950/40 dark:text-primary-300 dark:border-primary-800/50',
      in_progress:
        'bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800/50',
      completed:
        'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/50',
      cancelled:
        'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800/50',
      no_show:
        'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
    };
    return colors[status] || colors.pending;
  };

  const getBookingDotStyle = (dayBookings) => {
    if (!dayBookings.length) return null;
    const hasLive = dayBookings.some((b) => ['confirmed', 'in_progress'].includes(b.status));
    if (hasLive) {
      return { solid: 'bg-primary-500', title: 'Active booking on this date' };
    }
    return { solid: 'bg-emerald-500', title: 'Booking on this date' };
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      confirmed: 'Confirmed',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
      no_show: 'No Show'
    };
    return labels[status] || 'Pending';
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const date = new Date(timeString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isToday = (date) => date.toDateString() === new Date().toDateString();

  const legendStatuses = [
    { status: 'pending', label: 'Pending' },
    { status: 'confirmed', label: 'Confirmed' },
    { status: 'in_progress', label: 'In Progress' },
    { status: 'completed', label: 'Completed' },
    { status: 'cancelled', label: 'Cancelled' },
    { status: 'no_show', label: 'No Show' },
  ];

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Calendar"
        subtitle="See all events by month — click a day to open the booking."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={goToToday} size="sm" className="!rounded-xl">
              Today
            </Button>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={goToPreviousMonth}
                aria-label="Previous month"
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={goToNextMonth}
                aria-label="Next month"
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.calendar.title}
        steps={VENDOR_PAGE_GUIDES.calendar.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-200 dark:border-gray-800 px-4 py-3.5 sm:px-5">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-50 tracking-tight">
            {monthNames[currentMonth]} {currentYear}
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            {isLoading
              ? 'Loading events…'
              : error
                ? 'Unable to load events'
                : `${bookings.length} event${bookings.length === 1 ? '' : 's'} this month`}
          </p>
        </div>

        {isLoading ? (
          <div className="py-20 text-center px-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading calendar…</p>
          </div>
        ) : error ? (
          <div className="py-16 sm:py-20 text-center px-4">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-950/40 text-red-500">
              <AlertCircle className="w-7 h-7" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
              Error loading calendar
            </h3>
            <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto leading-relaxed">
              Unable to load your bookings calendar. Please try again shortly.
            </p>
          </div>
        ) : (
          <>
            {/* Days of week header */}
            <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-950/50">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div
                  key={day}
                  className="px-2 py-3 text-center text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7">
              {calendarDays.map((dayData, index) => {
                const today = dayData && isToday(dayData.date);
                return (
                  <div
                    key={index}
                    className={`relative min-h-[100px] sm:min-h-[120px] border-r border-b border-gray-100 dark:border-gray-800 p-2 sm:p-2.5 overflow-visible transition-colors ${
                      dayData
                        ? 'bg-white dark:bg-gray-900 hover:bg-gray-50/80 dark:hover:bg-gray-800/40'
                        : 'bg-gray-50/60 dark:bg-gray-950/40'
                    } ${(index + 1) % 7 === 0 ? 'border-r-0' : ''}`}
                  >
                    {dayData && (
                      <>
                        {/* Day number with in-cell event dot on the date */}
                        <div className="mb-2">
                          <div className="relative inline-flex">
                            <span
                              className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full transition-colors ${
                                today
                                  ? 'bg-primary-600 text-white ring-2 ring-primary-600/20 dark:ring-primary-400/30'
                                  : 'text-gray-900 dark:text-gray-100'
                              }`}
                            >
                              {dayData.day}
                            </span>
                            {(() => {
                              const dot = getBookingDotStyle(dayData.bookings);
                              if (!dot) return null;
                              return (
                                <span
                                  className="absolute -top-0.5 -right-0.5 flex h-2 w-2"
                                  title={dot.title}
                                  aria-hidden
                                >
                                  <span
                                    className={`relative inline-flex h-2 w-2 rounded-full ${dot.solid}`}
                                  />
                                </span>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Bookings for this day */}
                        <div className="space-y-1">
                          {dayData.bookings.slice(0, 3).map((booking) => (
                            <div
                              key={booking.id}
                              className={`text-xs p-1.5 sm:p-2 rounded-lg border cursor-pointer hover:shadow-sm hover:brightness-[0.98] dark:hover:brightness-110 transition-all ${getStatusColor(booking.status)}`}
                              onClick={() => navigate(`/vendor/bookings/${booking.id}`)}
                            >
                              <div className="flex items-center justify-between gap-1 mb-0.5">
                                <div className="flex items-center gap-1 min-w-0">
                                  <Clock className="w-3 h-3 shrink-0 opacity-70" />
                                  <span className="font-medium truncate">
                                    {formatTime(booking.start_time)}
                                  </span>
                                </div>
                                <span className="text-[10px] font-semibold uppercase tracking-wide shrink-0 opacity-80">
                                  {getStatusLabel(booking.status)}
                                </span>
                              </div>
                              <div className="font-mono text-[11px] font-medium truncate">
                                #{booking.booking_reference}
                              </div>
                              <div className="truncate text-[11px] opacity-90">
                                {booking.client_display_name || booking.guest_name || booking.client?.name || 'Client'}
                                {booking.booking_source === 'vendor_manual' && (
                                  <span className="ml-1 text-[10px] font-medium text-primary-600 dark:text-primary-400">
                                    (Private)
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}

                          {/* Show more indicator if there are more than 3 bookings */}
                          {dayData.bookings.length > 3 && (
                            <div className="text-[11px] text-gray-500 dark:text-gray-400 text-center py-1 font-medium">
                              +{dayData.bookings.length - 3} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {!isLoading && !error && bookings.length === 0 && (
              <div className="border-t border-gray-200 dark:border-gray-800 py-10 text-center px-4">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400">
                  <CalendarDays className="w-6 h-6" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                  No events this month
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
                  Bookings for {monthNames[currentMonth]} will appear on the days they are scheduled.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Legend */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm px-4 py-4 sm:px-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 shrink-0">
            Legend
          </h3>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
              <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              Active
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Other
            </span>
            {legendStatuses.map(({ status, label }) => (
              <span
                key={status}
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium border ${getStatusColor(status)}`}
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorCalendar;
